import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3Icon, PieChartIcon, AlertTriangleIcon, AlertCircleIcon, ArrowRightIcon, TrendingUpIcon, TrendingDownIcon, EyeIcon, CalendarIcon, FilterIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import StatusBadge from '../../components/ui/StatusBadge';
const TransactionRulesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<'day' | 'week' | 'month'>('day');
  // Mock data for the dashboard
  const dashboardData = {
    summary: {
      totalTransactions: 15782,
      flaggedTransactions: 243,
      riskAlerts: 68,
      highRiskMerchants: 12,
      activeRules: 24,
      ruleTriggersToday: 38
    },
    ruleViolationsByType: [{
      name: 'High Volume',
      count: 87,
      percentage: 35
    }, {
      name: 'Unusual Pattern',
      count: 62,
      percentage: 25
    }, {
      name: 'Merchant-Customer Pairing',
      count: 49,
      percentage: 20
    }, {
      name: 'Large Amount',
      count: 30,
      percentage: 12
    }, {
      name: 'Other',
      count: 15,
      percentage: 8
    }],
    recentAlerts: [{
      id: 'ALERT001',
      merchantName: 'Global Electronics',
      ruleViolated: 'High Transaction Volume',
      amount: 25000,
      timestamp: '2023-10-28T10:30:00Z',
      status: 'pending'
    }, {
      id: 'ALERT002',
      merchantName: 'City Grocers',
      ruleViolated: 'Same Customer Multiple Transactions',
      amount: 12500,
      timestamp: '2023-10-28T09:45:00Z',
      status: 'reviewing'
    }, {
      id: 'ALERT003',
      merchantName: 'Metro Pharmacy',
      ruleViolated: 'Unusual Transaction Pattern',
      amount: 8750,
      timestamp: '2023-10-28T08:15:00Z',
      status: 'resolved'
    }, {
      id: 'ALERT004',
      merchantName: 'Urban Clothing',
      ruleViolated: 'Large Transaction Amount',
      amount: 35000,
      timestamp: '2023-10-27T16:30:00Z',
      status: 'escalated'
    }],
    topMerchantsWithRisks: [{
      name: 'Global Electronics',
      alertCount: 12,
      riskScore: 78
    }, {
      name: 'City Grocers',
      alertCount: 8,
      riskScore: 65
    }, {
      name: 'Urban Clothing',
      alertCount: 7,
      riskScore: 62
    }, {
      name: 'Metro Pharmacy',
      alertCount: 5,
      riskScore: 58
    }, {
      name: 'Downtown Cafe',
      alertCount: 4,
      riskScore: 45
    }],
    riskTrend: {
      change: 5.2,
      direction: 'down'
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatNumber = (number: number) => {
    return new Intl.NumberFormat('en-US').format(number);
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <StatusBadge status="warning" text="Pending Review" data-id="element-6424" />;
      case 'reviewing':
        return <StatusBadge status="info" text="Under Review" data-id="element-6425" />;
      case 'resolved':
        return <StatusBadge status="success" text="Resolved" data-id="element-6426" />;
      case 'escalated':
        return <StatusBadge status="danger" text="Escalated" data-id="element-6427" />;
      default:
        return <StatusBadge status="default" text={status} data-id="element-6428" />;
    }
  };
  return <div className="space-y-6" data-id="element-6429">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-6430">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-6431">
          Transaction Rules Dashboard
        </h1>
        <div className="mt-3 sm:mt-0 flex items-center space-x-2" data-id="element-6432">
          <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6433">
            Time Range:
          </span>
          <div className="inline-flex rounded-md shadow-sm" data-id="element-6434">
            {(['day', 'week', 'month'] as const).map(range => <button key={range} onClick={() => setTimeRange(range)} className={`px-3 py-1 text-sm font-medium ${timeRange === range ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'} ${range === 'day' ? 'rounded-l-md' : ''} ${range === 'month' ? 'rounded-r-md' : ''} border border-gray-300 dark:border-gray-600`} data-id="element-6435">
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>)}
          </div>
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-6436">
        <Card className="bg-white dark:bg-gray-800" data-id="element-6437">
          <div className="flex items-center justify-between" data-id="element-6438">
            <div data-id="element-6439">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6440">
                Total Transactions
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-6441">
                {formatNumber(dashboardData.summary.totalTransactions)}
              </p>
              <div className="mt-1 flex items-center" data-id="element-6442">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6443">
                  {formatNumber(dashboardData.summary.flaggedTransactions)}{' '}
                  flagged
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-6444">
              <BarChart3Icon className="h-6 w-6 text-blue-600 dark:text-blue-400" data-id="element-6445" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-6446">
          <div className="flex items-center justify-between" data-id="element-6447">
            <div data-id="element-6448">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6449">
                Risk Alerts
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-6450">
                {dashboardData.summary.riskAlerts}
              </p>
              <div className="mt-1 flex items-center" data-id="element-6451">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-6452">
                  {dashboardData.summary.ruleTriggersToday} triggers today
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/30" data-id="element-6453">
              <AlertTriangleIcon className="h-6 w-6 text-orange-600 dark:text-orange-400" data-id="element-6454" />
            </div>
          </div>
        </Card>
        <Card className="bg-white dark:bg-gray-800" data-id="element-6455">
          <div className="flex items-center justify-between" data-id="element-6456">
            <div data-id="element-6457">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-6458">
                High Risk Merchants
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 dark:text-white" data-id="element-6459">
                {dashboardData.summary.highRiskMerchants}
              </p>
              <div className="mt-1 flex items-center" data-id="element-6460">
                {dashboardData.riskTrend.direction === 'down' ? <TrendingDownIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-6461" /> : <TrendingUpIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-6462" />}
                <span className={`text-sm font-medium ${dashboardData.riskTrend.direction === 'down' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`} data-id="element-6463">
                  {dashboardData.riskTrend.change}%
                </span>
              </div>
            </div>
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30" data-id="element-6464">
              <AlertCircleIcon className="h-6 w-6 text-red-600 dark:text-red-400" data-id="element-6465" />
            </div>
          </div>
        </Card>
      </div>
      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-6466">
        <Card className="lg:col-span-2" data-id="element-6467">
          <div className="flex justify-between items-center mb-4" data-id="element-6468">
            <h3 className="font-medium" data-id="element-6469">Rule Violations by Type</h3>
            <div className="flex items-center space-x-2" data-id="element-6470">
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-6471">
                <FilterIcon className="h-4 w-4 mr-1" data-id="element-6472" />
                Filter
              </button>
              <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-6473">
                <CalendarIcon className="h-4 w-4 mr-1" data-id="element-6474" />
                Date Range
              </button>
            </div>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-6475">
            <div className="text-center" data-id="element-6476">
              <BarChart3Icon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" data-id="element-6477" />
              <p className="mt-2 text-gray-500 dark:text-gray-400" data-id="element-6478">
                Rule violations chart visualization would appear here
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-2" data-id="element-6479">
            {dashboardData.ruleViolationsByType.map(type => <div key={type.name} className="flex items-center justify-between" data-id="element-6480">
                <span className="text-sm" data-id="element-6481">{type.name}</span>
                <div className="flex items-center" data-id="element-6482">
                  <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mr-2" data-id="element-6483">
                    <div className="bg-blue-600 h-2.5 rounded-full" style={{
                  width: `${type.percentage}%`
                }} data-id="element-6484"></div>
                  </div>
                  <span className="text-sm" data-id="element-6485">
                    {type.count} ({type.percentage}%)
                  </span>
                </div>
              </div>)}
          </div>
        </Card>
        <Card data-id="element-6486">
          <div className="flex justify-between items-center mb-4" data-id="element-6487">
            <h3 className="font-medium" data-id="element-6488">Top Merchants with Risks</h3>
            <button className="text-sm text-blue-600 dark:text-blue-400 hover:underline" data-id="element-6489">
              View All
            </button>
          </div>
          <div className="h-64 flex items-center justify-center" data-id="element-6490">
            <div className="text-center" data-id="element-6491">
              <PieChartIcon className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" data-id="element-6492" />
              <p className="mt-2 text-gray-500 dark:text-gray-400" data-id="element-6493">
                Risk distribution chart would appear here
              </p>
            </div>
          </div>
          <div className="mt-4 space-y-3" data-id="element-6494">
            {dashboardData.topMerchantsWithRisks.map((merchant, index) => <div key={merchant.name} className="flex items-center justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50" data-id="element-6495">
                <div className="flex items-center" data-id="element-6496">
                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center mr-2 text-xs font-medium" data-id="element-6497">
                    {index + 1}
                  </div>
                  <span className="text-sm font-medium" data-id="element-6498">{merchant.name}</span>
                </div>
                <div className="text-sm" data-id="element-6499">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${merchant.riskScore > 70 ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : merchant.riskScore > 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'}`} data-id="element-6500">
                    Score: {merchant.riskScore}
                  </span>
                </div>
              </div>)}
          </div>
        </Card>
      </div>
      {/* Recent Alerts */}
      <Card title="Recent Risk Alerts" data-id="element-6501">
        <div className="overflow-x-auto" data-id="element-6502">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6503">
            <thead className="bg-gray-50 dark:bg-gray-700" data-id="element-6504">
              <tr data-id="element-6505">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6506">
                  Alert ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6507">
                  Merchant
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6508">
                  Rule Violated
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6509">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6510">
                  Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6511">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider" data-id="element-6512">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" data-id="element-6513">
              {dashboardData.recentAlerts.map(alert => <tr key={alert.id} className="hover:bg-gray-50 dark:hover:bg-gray-750" data-id="element-6514">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" data-id="element-6515">
                    {alert.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6516">
                    {alert.merchantName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6517">
                    {alert.ruleViolated}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white" data-id="element-6518">
                    {formatCurrency(alert.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400" data-id="element-6519">
                    {formatDate(alert.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-6520">
                    {getStatusBadge(alert.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-6521">
                    <button onClick={() => navigate(`/transaction-rules/alerts/${alert.id}`)} className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300" data-id="element-6522">
                      <EyeIcon className="h-5 w-5" data-id="element-6523" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex justify-end" data-id="element-6524">
          <button onClick={() => navigate('/transaction-rules/alerts')} className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center" data-id="element-6525">
            View all alerts
            <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-6526" />
          </button>
        </div>
      </Card>
    </div>;
};
export default TransactionRulesDashboard;